// Rails entry point — `react_component 'WorkOrders/SendWorkOrderModal', { ... }`.
//
// The implementation lives in SendWorkOrderModalView.tsx (NOT named after the
// directory): webpack's require.context resolution treats a sibling file named
// after its parent directory as a self-import, yielding an undefined export at
// runtime (see Invoices/SendInvoiceModal and Proposals/SendProposal).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { showErrorToast } from '@ui';
import I18n from '@utils/i18n';
import api from '@utils/axios';
import { sendContentWorkOrderPath } from '@routes';
import { reloadWithToast } from '@utils/pendingToast';
import SendWorkOrderModalView from './SendWorkOrderModalView';
export { default as SendWorkOrderModal } from './SendWorkOrderModalView';
const AUTO_OPEN_PARAM = 'close_and_send';
export default function Index({ triggerIds = ['wo-close-and-send-report-link'], openOnLoad = false, workOrderId, sendToEmails }) {
    const [isOpen, setIsOpen] = useState(openOnLoad);
    const [content, setContent] = useState(null);
    const contentRequested = useRef(false);
    const triggerKey = triggerIds.join(',');
    // Fetched on hover, focus or open: building the fields refreshes a stale PDF,
    // which is too slow to do on every show page render.
    const prefetchPreview = useCallback(() => {
        if (contentRequested.current)
            return;
        contentRequested.current = true;
        // A positional id binds to the optional `(:locale)` scope; see docs/ai-knowledge/frontend/js-routes-locale-scope.md.
        const path = sendContentWorkOrderPath({
            id: workOrderId,
            format: 'json',
            _options: true
        });
        api
            .get(path)
            .then(({ data }) => {
            setContent(data);
            if (!data.pdfPath)
                return;
            const link = document.createElement('link');
            link.rel = 'prefetch';
            link.as = 'document';
            link.href = data.pdfPath;
            document.head.appendChild(link);
        })
            .catch(() => {
            contentRequested.current = false;
            setIsOpen(false);
            showErrorToast(String(I18n.t('generic.errors.request_error')));
        });
    }, [workOrderId]);
    useEffect(() => {
        if (!openOnLoad)
            return;
        prefetchPreview();
        const url = new URL(window.location.href);
        if (!url.searchParams.has(AUTO_OPEN_PARAM))
            return;
        url.searchParams.delete(AUTO_OPEN_PARAM);
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }, [openOnLoad, prefetchPreview]);
    useEffect(() => {
        const onClick = (event) => {
            event.preventDefault();
            prefetchPreview();
            setIsOpen(true);
        };
        const triggers = triggerKey
            .split(',')
            .filter(Boolean)
            .map((id) => document.getElementById(id))
            .filter((el) => el !== null);
        triggers.forEach((trigger) => {
            trigger.addEventListener('click', onClick);
            trigger.addEventListener('mouseenter', prefetchPreview);
            trigger.addEventListener('focus', prefetchPreview);
        });
        return () => {
            triggers.forEach((trigger) => {
                trigger.removeEventListener('click', onClick);
                trigger.removeEventListener('mouseenter', prefetchPreview);
                trigger.removeEventListener('focus', prefetchPreview);
            });
        };
    }, [triggerKey, prefetchPreview]);
    return (React.createElement(SendWorkOrderModalView, { key: content ? 'loaded' : 'loading', workOrderId: workOrderId, sendToEmails: sendToEmails, subject: content === null || content === void 0 ? void 0 : content.subject, body: content === null || content === void 0 ? void 0 : content.body, footer: content === null || content === void 0 ? void 0 : content.footer, pdfPath: content === null || content === void 0 ? void 0 : content.pdfPath, contentLoading: !content, isOpen: isOpen, onClose: () => setIsOpen(false), onSent: (message) => reloadWithToast(message) }));
}
